import { fetchClient } from '@/libs/api/fetchClient'
import type { BaseApiResponse } from '@/types/common/api'
import { User, ChangeUserStatusRequest, ChangeUserStatusResponse } from '@/types/setting/user'

/**
 * 获取用户列表
 * @returns Promise<{rows: User[], total: number} | null>
 */
export const fetchUserList = async (params?: {
  pageIndex?: number
  pageSize?: number
}): Promise<{ rows: User[], total: number } | null> => {
  try {

    const queryParams = new URLSearchParams()
    // 添加分页参数
    if (params?.pageIndex !== undefined) {
      queryParams.append('pageNum', params.pageIndex.toString())
    }
    if (params?.pageSize !== undefined) {
      queryParams.append('pageSize', params.pageSize.toString())
    }

    const queryString = queryParams.toString()
    const path = queryString ? `/user/list?${queryString}` : '/user/list'
    const response = await fetchClient<{ rows: User[], total: number }>({
      path: path,
      method: 'GET'
    })

    // 直接返回API响应，因为数据结构是{rows, total}
    return response.data || { rows: [], total: 0 }
  } catch (error) {
    console.error('获取用户数据失败:', error)
    return null
  }
}


/**
 * 更新用户信息
 * @param userData 用户数据
 * @returns Promise<boolean>
 */
export const updateUser = async (body: User): Promise<BaseApiResponse> => {
  try {
    const response = await fetchClient<BaseApiResponse>({
      path: '/user',
      method: 'PUT',
      body
    })
    return response
  } catch (error) {
    console.error('更新用户失败:', error)
    return {
      msg: '更新用户失败',
      code: 500,
      status: 'error'
    }
  }
}

/**
 * 创建新用户
 * @param body 用户数据
 * @returns Promise<boolean>
 */
export const createUser = async (body: User): Promise<BaseApiResponse> => {
  try {
    const response = await fetchClient<BaseApiResponse>({
      path: '/user',
      method: 'POST',
      body
    })
    return response
  } catch (error) {
    console.error('创建用户失败:', error)
    return {
      msg: 'Create user failed',
      code: 500,
      status: 'error'
    }
  }
}

/**
 * 更改用户状态
 * @param request 请求参数，包含userId和status
 * @returns Promise<ChangeUserStatusResponse>
 */
export const changeUserStatus = async (request: ChangeUserStatusRequest): Promise<BaseApiResponse> => {
  try {
    // 使用BaseApiResponse作为正确的泛型类型
    const response = await fetchClient<BaseApiResponse>({
      path: '/user/changeStatus',
      method: 'PUT',
      body: request
    })
    // 确保返回的数据符合ChangeUserStatusResponse结构
    return {
      msg: response.msg || '操作成功',
      code: response.code || 200,
      success: response.success
    }
  } catch (error) {
    console.error('更改用户状态失败:', error)
    throw error
  }
}

/**
 * 重置用户密码
 * @param request 请求参数，包含userId和status
 * @returns Promise<ChangeUserStatusResponse>
 */
export const resetPwd = async (request: { userId: number, password: string }): Promise<BaseApiResponse> => {
  try {
    // 使用BaseApiResponse作为正确的泛型类型
    const response = await fetchClient<BaseApiResponse>({
      path: '/user/resetPwd',
      method: 'PUT',
      body: request
    })
    // 确保返回的数据符合ChangeUserStatusResponse结构
    return {
      msg: response.msg || '操作成功',
      code: response.code || 200,
      success: response.success
    }
  } catch (error) {
    console.error('重置用户密码失败:', error)
    throw error
  }
}

/**
 * 删除用户
 * @param request 请求参数，包含userId
 * @returns Promise<ChangeUserStatusResponse>
 */
export const deleteUser = async (request: { userId: number }): Promise<BaseApiResponse> => {
  try {
    // 使用BaseApiResponse作为正确的泛型类型
    const response = await fetchClient<BaseApiResponse>({
      path: `/user/${request.userId}`,
      method: 'DELETE'
    })
    // 确保返回的数据符合ChangeUserStatusResponse结构
    return {
      msg: response.msg || '操作成功',
      code: response.code || 200,
      success: response.success
    }
  } catch (error) {
    console.error('删除用户失败:', error)
    throw error
  }
}

export const updatePwd = async (request: { oldPassword: string, newPassword: string }): Promise<BaseApiResponse> => {
  try {
    // 使用BaseApiResponse作为正确的泛型类型
    const response = await fetchClient<BaseApiResponse>({
      path: '/user/updatePwd',
      method: 'PUT',
      body: request
    })
    // 确保返回的数据符合ChangeUserStatusResponse结构
    return {
      msg: response.msg || '操作成功',
      code: response.code || 200,
      success: response.success
    }
  } catch (error) {
    console.error('更新用户密码失败:', error)
    throw error
  }
}

export const getRouters = async (): Promise<BaseApiResponse> => {
  try {
    // 使用BaseApiResponse作为正确的泛型类型
    const response = await fetchClient<BaseApiResponse>({
      path: '/menu/getRouters',
      method: 'GET'
    })
    // 确保返回的数据符合ChangeUserStatusResponse结构
    return {
      msg: response.msg || '操作成功',
      code: response.code || 200,
      success: response.success,
      data: response.data || []
    }
  } catch (error) {
    console.error('更新用户密码失败:', error)
    throw error
  }
}